class CartDrawer extends HTMLElement {
  constructor() {
    super(); 

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    const inner = this.querySelector('.drawer__inner');
    if (inner) inner.classList.remove('is-empty');
    // The element-level class must clear too (refreshAndOpen already does):
    // cart-drawer.is-empty CSS hides .drawer__header and grids .drawer__inner,
    // so leaving it stranded after an add-from-empty renders a full cart with
    // no header in the empty-state layout. (Audit find, 2026-09-01.)
    this.classList.remove('is-empty');
    this.productId = parsedState.id;

    // By the time we get here the item is already in the cart, so a missing or
    // unparseable section payload must never stop us reaching open() below. It
    // used to throw mid-loop, the drawer never appeared, and the shopper tapped
    // Add to cart again and bought the same thing twice.
    let missingSection = false;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;

      const html = this.getSectionInnerHTML((parsedState.sections || {})[section.id], section.selector);
      if (html === null) {
        missingSection = true;
        return;
      }
      sectionElement.innerHTML = html;
    });

    if (missingSection) {
      this.refreshAndOpen();
      return;
    }

    setTimeout(() => {
      const overlay = this.querySelector('#CartDrawer-Overlay');
      if (overlay) overlay.addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  // Self-heal: pull a fresh drawer render straight from the section endpoint and
  // open it, so the shopper still sees their item land when the inline payload
  // was unusable. Section renders can be cached, hence the cache-bust + no-store.
  refreshAndOpen() {
    const url = `${window.location.pathname}?section_id=cart-drawer&_=${Date.now()}`;

    return fetch(url, { cache: 'no-store' })
      .then((response) => (response.ok ? response.text() : Promise.reject(new Error(`section ${response.status}`))))
      .then((text) => {
        const html = this.getSectionInnerHTML(text, '#CartDrawer');
        const target = document.querySelector('#CartDrawer');
        if (html !== null && target) target.innerHTML = html;
      })
      .catch((e) => {
        console.error('cart drawer refresh failed', e);
      })
      .finally(() => {
        this.classList.remove('is-empty');
        const inner = this.querySelector('.drawer__inner');
        if (inner) inner.classList.remove('is-empty');
        const overlay = this.querySelector('#CartDrawer-Overlay');
        if (overlay) overlay.addEventListener('click', this.close.bind(this));
        this.open();
      });
  }

  // Resolves once the drawer is genuinely on screen, or after a safety timeout so
  // an Add to cart button waiting on it can never be left stuck disabled.
  whenVisible(timeout = 4000) {
    if (this.classList.contains('active')) return Promise.resolve(true);

    return new Promise((resolve) => {
      let settled = false;
      const settle = (visible) => {
        if (settled) return;
        settled = true;
        observer.disconnect();
        clearTimeout(timer);
        resolve(visible);
      };

      const observer = new MutationObserver(() => {
        if (this.classList.contains('active')) settle(true);
      });
      observer.observe(this, { attributes: true, attributeFilter: ['class'] });
      const timer = setTimeout(() => settle(false), timeout);
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    if (typeof html !== 'string' || html === '') return null;
    const section = new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
    return section ? section.innerHTML : null;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
